import { useEffect, useRef, useState } from 'react';

import { PC_SEARCH_HOME_SCROLL_PADDING_TOP } from '~constants/common';
import { PASS_CARD_SECTION_DIVIDER_HEIGHT } from '~constants/pass';

import { useDetermineMobile } from '@myrealtrip/device-detect';

interface SectionRegisterOption<T extends string> {
  key: T;
  node: HTMLElement;
}

interface Params<T extends string> {
  sectionKeys: T[];
  threshold?: number;
}

const useAnchorSectionObserver = <T extends string>({ sectionKeys, threshold = 0 }: Params<T>) => {
  const isMobile = useDetermineMobile();

  const [inViewSectionKey, setInViewSectionKey] = useState<T>(sectionKeys[0]);
  const sectionRefs = useRef<SectionRegisterOption<T>[]>([]);
  const isScrolling = useRef(false);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const subscribe = (key: T) => (node: HTMLElement | null) => {
    const isExist = sectionRefs.current.some((section) => section.key === key);

    if (!isExist && node) {
      sectionRefs.current.push({ key, node });
    }
  };

  const scrollTo = (sectionKey: T) => {
    const targetSection = sectionRefs.current.find((section) => section.key === sectionKey);
    if (!targetSection?.node) return;

    isScrolling.current = true;
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

    setInViewSectionKey(sectionKey);

    const top = isMobile
      ? targetSection.node.offsetTop - threshold + PASS_CARD_SECTION_DIVIDER_HEIGHT
      : targetSection.node.offsetTop - threshold;
    window.scrollTo({ top, behavior: 'smooth' });

    scrollTimeout.current = setTimeout(() => {
      isScrolling.current = false;
    }, 1000);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isScrolling.current) return;

      const latest = window.scrollY;

      for (const section of sectionRefs.current) {
        if (!section.node) {
          return;
        }

        const top = isMobile
          ? section.node.offsetTop - threshold + PASS_CARD_SECTION_DIVIDER_HEIGHT
          : section.node.offsetTop - threshold - PC_SEARCH_HOME_SCROLL_PADDING_TOP;

        if (latest === 0) {
          setInViewSectionKey(sectionKeys[0]);
        }

        if (latest >= top) {
          setInViewSectionKey(section.key);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    // 마운트 시 한 번 실행 (초기 위치 반영)
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, []);

  return { subscribe, scrollTo, inViewSectionKey };
};

export default useAnchorSectionObserver;
